import { useState } from 'react'
import { router } from '@inertiajs/react'
import LogDetailsDialog from './LogDetailsDialog'
import { BarChart2, User } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { DataFieldChartDialog, hasChartableDataFieldType } from '../shared/DataFieldChartDialog'
import { t } from '@/shared/lib/i18n'
import { CONFIDENTIAL_MASK } from '@/shared/lib/confidential-mask'
import { getTaskLogStatusDisplay } from '@/shared/lib/taskLogStatusDisplay'
import { RICH_TEXT_DISPLAY_CLASSNAME, sanitizeRichTextHtml } from '@/shared/lib/rich-text-html'
import type { WebInstrumentCalendarView } from '@/shared/types/web'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'

export type TaskVersionEntry = WebInstrumentCalendarView['taskRows'][number]['taskVersions'][number]

type ExecutionLogEntry = NonNullable<TaskVersionEntry['executionLogs']>[number]

export interface ChangeLogExecutionStatusDisplay {
  label: string
  className: string
  dotClassName: string
}

/** Maps change-log execution status (+ dateModified) onto shared Complete pills; failed keeps local amber styling. */
export const getChangeLogExecutionStatusDisplay = (
  executionLog: ExecutionLogEntry,
): ChangeLogExecutionStatusDisplay => {
  if (executionLog.status === 'npt') {
    const isDateModified = executionLog.dateModified === true
    const { label, className } = getTaskLogStatusDisplay({
      isNpt: true,
      qcPassed: false,
      dateModified: isDateModified,
    })
    return { label, className, dotClassName: isDateModified ? 'bg-amber-500' : 'bg-gray-400' }
  }

  if (executionLog.status !== 'complete') {
    return {
      label: t('instruments.show.history.taskFailed'),
      className: 'bg-amber-100 text-amber-800 border-amber-200',
      dotClassName: 'bg-amber-500',
    }
  }

  const isDateModified = executionLog.dateModified === true
  const { label, className } = getTaskLogStatusDisplay({
    isNpt: false,
    qcPassed: true,
    dateModified: isDateModified,
  })

  return {
    label,
    className,
    dotClassName: isDateModified ? 'bg-amber-500' : 'bg-green-500',
  }
}

interface Props {
  instrumentId: number
  instrumentLabel: string
  serialNumber: string | null
  taskName: string | null
  taskVersions: TaskVersionEntry[]
  onClose: () => void
}

const TaskChangeLogDialog = ({
  instrumentId,
  instrumentLabel,
  serialNumber,
  taskName,
  taskVersions,
  onClose,
}: Props) => {
  const isOpen = taskName !== null
  const headerTitle = serialNumber ? `${instrumentLabel} (${serialNumber})` : instrumentLabel

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-normal text-muted-foreground">
            {headerTitle}
          </DialogTitle>
          <p className="text-base font-semibold">{t('instruments.show.tasks.changeLog.title')}</p>
        </DialogHeader>

        {taskVersions.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t('instruments.show.tasks.changeLog.empty')}
          </p>
        ) : (
          <div className="space-y-6">
            {taskVersions.map((version) => (
              <ChangeLogVersionCard
                key={version.taskId}
                instrumentId={instrumentId}
                version={version}
              />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export const ChangeLogVersionCard = ({
  instrumentId,
  version,
}: {
  instrumentId: number
  version: TaskVersionEntry
}) => {
  const { formatDateTime } = useSiteDateTime()
  const [executionLog] = version.executionLogs ?? []
  const [chartOpen, setChartOpen] = useState(false)
  const [openLogId, setOpenLogId] = useState<number | null>(null)
  const showViewChart = version.dataFields.some((field) =>
    hasChartableDataFieldType(field.dataFieldType),
  )
  const executionStatusDisplay = executionLog
    ? getChangeLogExecutionStatusDisplay(executionLog)
    : null
  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <div className="space-y-1 text-sm leading-[23px]">
        {executionLog && executionStatusDisplay && (
          <>
            <div>
              <button
                type="button"
                data-testid="change-log-execution-status"
                aria-label={`View log details: ${executionStatusDisplay.label}`}
                title={`View log details: ${executionStatusDisplay.label}`}
                onClick={() => setOpenLogId(executionLog.instrumentTaskLogId)}
                className={`inline-flex cursor-pointer items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium transition-opacity hover:opacity-75 ${executionStatusDisplay.className}`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${executionStatusDisplay.dotClassName}`}
                  aria-hidden="true"
                />
                {executionStatusDisplay.label}
              </button>
              {version.taskInactive && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {t('instruments.reports.cell.inactiveTaskSuffix')}
                </span>
              )}
            </div>
            <p>
              <span className="font-medium">{t('instruments.show.history.performedAt')}:</span>{' '}
              <span className="text-muted-foreground">
                {formatDateTime(executionLog.performedAt)}
              </span>
            </p>
            <p>
              <span className="font-medium">{t('instruments.show.history.performedBy')}:</span>{' '}
              <span className="text-muted-foreground">
                {executionLog.completedByName ? (
                  <>
                    <User className="mr-0.5 inline h-3.5 w-3.5 align-middle" />
                    {executionLog.completedByName}
                  </>
                ) : (
                  '—'
                )}
              </span>
            </p>
            {executionLog.dateModified && executionLog.modReason && (
              <div>
                <span className="font-medium">
                  {t('instruments.show.history.dateModifiedReason')}:
                </span>
                <div
                  className={RICH_TEXT_DISPLAY_CLASSNAME}
                  dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(executionLog.modReason) }}
                />
              </div>
            )}
            {version.instrumentTypeName && (
              <p>
                <span className="font-medium">
                  {t('instruments.show.tasks.changeLog.instrumentType')}:
                </span>{' '}
                <span className="text-muted-foreground">{version.instrumentTypeName}</span>
              </p>
            )}
          </>
        )}
        <p>
          <span className="font-medium">{t('instruments.show.tasks.changeLog.created')}:</span>{' '}
          <span className="text-muted-foreground">
            {version.createdAt ? formatDateTime(version.createdAt) : '—'}
            {version.createdByName && (
              <>
                {' '}
                <User className="mr-0.5 inline h-3.5 w-3.5 align-middle" />
                {version.createdByName}
              </>
            )}
          </span>
        </p>
        {version.taskInactive && version.endAt && (
          <p>
            <span className="font-medium">{t('instruments.show.tasks.changeLog.updatedBy')}:</span>{' '}
            <span className="text-muted-foreground">
              {formatDateTime(version.endAt)}
              {version.updatedByName && (
                <>
                  {' '}
                  <User className="mr-0.5 inline h-3.5 w-3.5 align-middle" />
                  {version.updatedByName}
                </>
              )}
            </span>
          </p>
        )}
      </div>

      <details className="rounded border bg-background text-sm">
        <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
          {t('instruments.show.tasks.changeLog.taskName')}: {version.taskName}
        </summary>
        <div className="space-y-1 border-t px-3 pb-3 pt-2">
          <div className="space-y-1 leading-[23px]">
            <p>
              <span className="font-medium">{t('instruments.show.tasks.changeLog.taskName')}:</span>{' '}
              {version.taskName}
            </p>
            <p>
              <span className="font-medium">
                {t('instruments.show.tasks.changeLog.frequencyType')}:
              </span>{' '}
              {version.frequencyTypeName ?? '—'}
            </p>
            {version.frequencyTypeCode !== 'UNSCHEDULED' && version.intervalName && (
              <p>
                <span className="font-medium">
                  {t('instruments.show.tasks.changeLog.frequency')}:
                </span>{' '}
                {version.intervalName}
              </p>
            )}
            {version.frequencyTypeCode === 'CALENDAR_FIXED' && version.startAt && (
              <p>
                <span className="font-medium">
                  {t('instruments.show.tasks.changeLog.startAt')}:
                </span>{' '}
                {version.startAt}
              </p>
            )}
            {version.frequencyTypeCode === 'CALENDAR_FIXED' && (
              <div>
                <span className="font-medium">
                  {t('instruments.show.tasks.changeLog.endsAtMidnight')}:
                </span>{' '}
                <Badge variant={version.endsAtMidnight ? 'default' : 'secondary'}>
                  {version.endsAtMidnight
                    ? t('instruments.show.tasks.changeLog.yes')
                    : t('instruments.show.tasks.changeLog.no')}
                </Badge>
              </div>
            )}
            <div>
              <span className="font-medium">
                {t('instruments.show.tasks.changeLog.affectsCompliance')}:
              </span>{' '}
              <Badge variant={version.affectsCompliance ? 'default' : 'secondary'}>
                {version.affectsCompliance
                  ? t('instruments.show.tasks.changeLog.yes')
                  : t('instruments.show.tasks.changeLog.no')}
              </Badge>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="font-medium">
                {t('instruments.show.tasks.changeLog.dataFields')}:
              </span>
              {showViewChart && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setChartOpen(true)}
                >
                  <BarChart2 className="h-3.5 w-3.5 mr-1.5" />
                  {t('instruments.show.tasks.viewChart')}
                </Button>
              )}
            </div>
            {version.dataFields.length === 0 ? (
              <span className="ml-1 text-muted-foreground">
                {t('instruments.show.tasks.changeLog.none')}
              </span>
            ) : (
              <Table className="mt-1">
                <TableHeader>
                  <TableRow>
                    <TableHead className="py-1 text-xs">
                      {t('instruments.show.tasks.changeLog.field')}
                    </TableHead>
                    <TableHead className="py-1 text-xs">
                      {t('instruments.show.tasks.changeLog.fieldType')}
                    </TableHead>
                    <TableHead className="py-1 text-xs">
                      {t('instruments.show.tasks.changeLog.validateField')}
                    </TableHead>
                    {executionLog && (
                      <TableHead className="py-1 text-xs">
                        {t('instruments.show.tasks.changeLog.enteredValue')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {version.dataFields.map((field, index) => {
                    const enteredValue = executionLog?.dataFieldValues?.find(
                      (v) => v.instrumentTaskDataFieldId === field.instrumentTaskDataFieldId,
                    )
                    return (
                      <TableRow key={`${field.label ?? ''}-${index}`}>
                        <TableCell className="py-1 text-xs">{field.label ?? '—'}</TableCell>
                        <TableCell className="py-1 text-xs">{field.dataFieldType ?? '—'}</TableCell>
                        <TableCell className="py-1 text-xs">
                          {field.acceptable ?? t('instruments.show.tasks.changeLog.none')}
                        </TableCell>
                        {executionLog && (
                          <TableCell className="py-1 text-xs">
                            {enteredValue?.isConfidentialHidden ? (
                              <span title={t('common.confidentialData')}>{CONFIDENTIAL_MASK}</span>
                            ) : (
                              (enteredValue?.value ?? '—')
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </details>

      {chartOpen && (
        <DataFieldChartDialog
          onClose={() => setChartOpen(false)}
          instrumentId={instrumentId}
          instrumentTaskId={version.taskId}
          taskName={version.taskName}
        />
      )}

      {openLogId != null && (
        <LogDetailsDialog
          instrumentId={instrumentId}
          instrumentTaskId={version.taskId}
          taskLogId={openLogId}
          onClose={() => setOpenLogId(null)}
          onAfterDelete={() => {
            setOpenLogId(null)
            router.reload({ only: ['taskVersionHistory'] })
          }}
        />
      )}
    </div>
  )
}

export default TaskChangeLogDialog
